import { Component, input, output, type AfterViewInit, type OnDestroy, ViewEncapsulation, ChangeDetectionStrategy, ElementRef, viewChild } from '@angular/core';
import Cropper from 'cropperjs';

@Component({
  selector: 'lib-cropper',
  imports: [],
  template: `
    <div class="cropper-backdrop" (click)="onCancel()">
      <div class="cropper-panel" (click)="$event.stopPropagation()">
        <!-- Header -->
        <div class="cropper-header">
          <h2 class="cropper-title">Recortar foto</h2>
          <p class="cropper-subtitle">Ajusta el recorte cuadrado y confirma</p>
        </div>

        <!-- Imagen con cropperjs -->
        <div class="cropper-wrapper">
          <img
            #cropImage
            [src]="imageSrc()"
            alt="Recortar foto"
            class="cropper-image"
          />
        </div>

        <!-- Acciones -->
        <div class="cropper-actions">
          <button class="btn-cancel" (click)="onCancel()">Cancelar</button>
          <button class="btn-confirm" (click)="onConfirm()">Confirmar</button>
        </div>
      </div>
    </div>
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Default,
  styles: [`
    :host { display: contents; }

    .cropper-backdrop {
      position: fixed;
      inset: 0;
      background: var(--cr-backdrop, rgba(0, 0, 0, 0.85));
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      backdrop-filter: blur(4px);
      padding: 12px;
    }

    .cropper-panel {
      background: #0D1F15;
      border: 1px solid #1A3A22;
      border-radius: 20px;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);

      /* Mobile first: compacto */
      padding: 16px;
      gap: 12px;
      width: min(94vw, 100%);
      height: min(94dvh, 480px);
      display: flex;
      flex-direction: column;
    }

    .cropper-header {
      text-align: center;
      flex-shrink: 0;
    }

    .cropper-title {
      font-family: 'Oswald', system-ui, sans-serif;
      font-size: clamp(1rem, 4vw, 1.25rem);
      font-weight: 700;
      color: #FFFFFF;
      margin: 0;
    }

    .cropper-subtitle {
      font-size: clamp(0.7rem, 2.5vw, 0.8rem);
      color: #8CA893;
      margin: 2px 0 0;
    }

    /* ── CSS Custom Properties (tema) ── */

    .cropper-panel {
      background: var(--cr-panel-bg, #0D1F15);
      border-color: var(--cr-panel-border, #1A3A22);
      box-shadow: 0 20px 60px var(--cr-panel-shadow, rgba(0, 0, 0, 0.5));
    }

    .cropper-title {
      color: var(--cr-title-color, #FFFFFF);
    }

    .cropper-subtitle {
      color: var(--cr-subtitle-color, #8CA893);
    }

    .cropper-wrapper {
      background: var(--cr-wrapper-bg, #06110A);
      position: relative;
      overflow: hidden;
      border-radius: 12px;
      width: 100%;
      height: 320px;
    }

    .cropper-wrapper > cropper-canvas {
      display: block;
      width: 100%;
      height: 100%;
    }

    .cropper-wrapper > cropper-canvas > cropper-image {
      display: block;
      width: 100%;
      height: 100%;
    }

    .cropper-actions {
      display: flex;
      gap: 10px;
      justify-content: stretch;
      flex-shrink: 0;
    }

    .btn-confirm,
    .btn-cancel {
      flex: 1;
      font-family: 'Oswald', system-ui, sans-serif;
      font-weight: 700;
      font-size: clamp(0.875rem, 3vw, 1rem);
      padding: 12px 20px;
      border-radius: 10px;
      cursor: pointer;
      transition: all 0.2s ease;
      text-align: center;
      border: none;
    }

    .btn-confirm {
      background: var(--cr-confirm-bg, linear-gradient(135deg, #93F64E 0%, #6DD835 100%));
      color: var(--cr-confirm-color, #06110A);
    }

    .btn-confirm:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px var(--cr-confirm-shadow, rgba(147, 246, 78, 0.35));
    }

    .btn-cancel {
      background: transparent;
      color: var(--cr-cancel-color, #8CA893);
      border: 1px solid var(--cr-cancel-border, #1A3A22);
    }

    .btn-cancel:hover {
      background: var(--cr-cancel-hover-bg, rgba(255, 255, 255, 0.05));
      color: var(--cr-cancel-hover-color, #FFFFFF);
    }

    /* Tablet */
    @media (min-width: 768px) {
      .cropper-panel {
        padding: 24px;
        gap: 16px;
        width: min(88vw, 640px);
        height: min(90vh, 620px);
      }

      .cropper-wrapper {
        height: 420px;
      }

      .cropper-actions {
        justify-content: flex-end;
      }

      .btn-confirm,
      .btn-cancel {
        flex: 0 0 auto;
        padding: 12px 32px;
        font-size: 1rem;
      }
    }

    /* Desktop — ~600px para el área de recorte */
    @media (min-width: 1200px) {
      .cropper-panel {
        padding: 28px;
        gap: 18px;
        width: min(80vw, 700px);
        height: min(88vh, 700px);
      }

      .cropper-wrapper {
        height: 600px;
      }
    }
  `],
})
export class CropperComponent implements AfterViewInit, OnDestroy {
  /** URL de la imagen a recortar (data URL) */
  readonly imageSrc = input.required<string>();

  /** Razón de aspecto del recorte (ancho/alto) */
  readonly aspectRatio = input<number>(1);

  /** Emite el Blob recortado cuando el usuario confirma */
  readonly confirm = output<Blob>();

  /** Emite cuando el usuario cancela */
  readonly cancel = output<void>();

  /** Referencia a la imagen donde cropperjs se inicializa */
  readonly cropImageRef = viewChild<ElementRef<HTMLImageElement>>('cropImage');

  private cropper: Cropper | null = null;

  ngAfterViewInit(): void {
    this.initCropper();
  }

  ngOnDestroy(): void {
    this.destroyCropper();
  }

  private initCropper(): void {
    const imgEl = this.cropImageRef()?.nativeElement;
    if (!imgEl) return;

    // Esperar a que la imagen esté cargada antes de init cropper
    if (imgEl.complete && imgEl.naturalWidth > 0) {
      this.createCropper(imgEl);
    } else {
      imgEl.onload = () => this.createCropper(imgEl);
    }
  }

  private createCropper(img: HTMLImageElement): void {
    // Inicializar CropperJS v2 (Web Components-based)
    this.cropper = new Cropper(img);

    // Configurar la selección después de que el DOM de componentes se haya actualizado
    requestAnimationFrame(() => {
      const selection = this.cropper?.getCropperSelection();
      if (selection) {
        selection.aspectRatio = this.aspectRatio();
        selection.initialCoverage = 0.95;
      }
    });
  }

  async onConfirm(): Promise<void> {
    if (!this.cropper) return;

    const selection = this.cropper.getCropperSelection();
    if (!selection) return;

    try {
      const canvas = await selection.$toCanvas({
        width: 512,
        height: 512,
      });

      canvas.toBlob((blob: Blob | null) => {
        if (blob) {
          this.confirm.emit(blob);
        }
        this.destroyCropper();
      }, 'image/jpeg', 0.92);
    } catch {
      this.destroyCropper();
    }
  }

  onCancel(): void {
    this.cancel.emit();
    this.destroyCropper();
  }

  private destroyCropper(): void {
    this.cropper?.destroy();
    this.cropper = null;
  }
}
